import {
  CloseOutlined,
  DoneOutlineOutlined,
  ErrorOutlineOutlined,
} from '@mui/icons-material';
import {
  TableRow,
  TableCell,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import { upperFirst } from 'lodash';

import { tss } from 'tss-react/mui';

import type { Dispatch, SetStateAction } from 'react';

import { graphql, type DocumentType } from '@/gql';

import { FileIcon } from '../../../FileIcon';

import type { FileManagerUploadingState } from '../../FileManagerUploadingBar';

import { UploadingFileParentCell } from './uploading-file-row/UploadingFileParentCell';

const UPLOADING_FILE_ROW_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment uploadingFileRowFilesForOwnerFields on FilesForOwner {
    ...uploadingFileParentCellFilesForOwnerFields
  }
`);

export type UploadingFile = Readonly<{
  folder: boolean;
  zip: boolean;
  status?: string;
  filename: string;
  progress: number;
  parent: string | null | undefined;
  error?: string;
}>;

interface Props {
  file: UploadingFile;
  filesForOwner: DocumentType<
    typeof UPLOADING_FILE_ROW_FILES_FOR_OWNER_FRAGMENT
  >;
  setUploadingState: Dispatch<SetStateAction<FileManagerUploadingState>>;
}

const useStyles = tss.create(({ theme }) => ({
  progress: {
    textAlign: 'right',
  },

  uploadingProgress: {
    display: 'flex',
    justifyContent: 'flex-end',
  },

  uploadingSpinner: {
    marginLeft: theme.spacing(1),
  },

  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}));

export const UploadingFileRow = ({
  file,
  filesForOwner,
  setUploadingState,
}: Props) => {
  const { classes } = useStyles();
  const { progress: totalProgress } = file;

  const isWaiting =
    !file.error &&
    (!totalProgress ||
      (file.status && ['extracting', 'creating'].includes(file.status)));

  const isDone = !isWaiting && Boolean(file.error || totalProgress === 1);
  const isMidUpload = !(isWaiting || isDone);

  return (
    <TableRow>
      <TableCell padding="none">
        <FileIcon
          file={{
            __typename: 'File',
            filename: file.filename,
            folder: file.folder,
            url: null,
          }}
        />{' '}
        {file.filename}
      </TableCell>
      <TableCell>
        in <UploadingFileParentCell file={file} filesForOwner={filesForOwner} />
      </TableCell>
      <TableCell className={classes.progress} padding="none">
        {isWaiting && (
          <div className={classes.uploadingProgress}>
            {file.status && (
              <Typography variant="caption">
                {upperFirst(file.status)}
              </Typography>
            )}
            <CircularProgress size={18} className={classes.uploadingSpinner} />
          </div>
        )}
        {isMidUpload && (
          <div className={classes.uploadingProgress}>
            <Typography variant="caption">
              {Math.floor(totalProgress * 100)}%
            </Typography>
            <CircularProgress
              size={18}
              variant="determinate"
              value={totalProgress * 100}
              className={classes.uploadingSpinner}
            />
          </div>
        )}
        {isDone && (
          <div className={classes.actions}>
            {file.error ? (
              <Tooltip title={humanizeErrorMessage(file.error)}>
                <ErrorOutlineOutlined color="error" />
              </Tooltip>
            ) : (
              <DoneOutlineOutlined />
            )}
            <Tooltip title="Clear">
              <IconButton
                onClick={() => {
                  setUploadingState((prevState) => {
                    return {
                      ...prevState,
                      uploadingFiles: prevState.uploadingFiles.filter(
                        (f) => f.filename !== file.filename,
                      ),
                    };
                  });
                }}
                size="large"
              >
                <CloseOutlined />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

function humanizeErrorMessage(err: string) {
  if (/ALREADY_EXISTS/.test(err)) {
    return 'Unable to create file/folder - it already exists';
  }

  return err;
}
